export interface QuickAccessItem {
  key: string
  path: string
  emoji: string
  labelKey: string
  roles: ('coach' | 'trainee' | 'any')[]
  accent?: 'brand' | 'green' | 'orange' | 'red' | 'blue'
  condition?: 'hasCoaches'
  pinnable?: boolean
}

export interface MoreSectionConfig {
  key: string
  titleKey: string
  roles: ('coach' | 'trainee')[]
  itemKeys: string[]
  condition?: 'hasCoaches'
}

export const QUICK_ACCESS_CATALOG: QuickAccessItem[] = [
  { key: 'this-week-trainings',    path: '/this-week-trainings',       emoji: '📅', labelKey: 'this-week-trainings',    roles: ['any'] },
  { key: 'all-training-sets',      path: '/all-training-sets',         emoji: '📋', labelKey: 'all-training-sets',      roles: ['coach'] },
  { key: 'active-training-set',     path: '/active-training-set',       emoji: '📋', labelKey: 'active-training-set',     roles: ['trainee'], accent: 'blue', condition: 'hasCoaches' },
  { key: 'training-blocks',        path: '/training-blocks',           emoji: '🏋️', labelKey: 'training-blocks',        roles: ['any'] },
  { key: 'training-programs',      path: '/training-programs',         emoji: '🗂️', labelKey: 'training-programs',      roles: ['any'] },
  { key: 'training-schedule',      path: '/training-schedule',      emoji: '⏱️', labelKey: 'training-schedule',      roles: ['any'],     pinnable: false },
  { key: 'start-training',         path: '/training/start',          emoji: '🔥', labelKey: 'start-training',         roles: ['any'],     accent: 'orange' },
  { key: 'program-history',        path: '/history',                   emoji: '🕑', labelKey: 'program-history',        roles: ['any'] },
  { key: 'pro-view',               path: '/pro-view',                  emoji: '⚡', labelKey: 'pro-view',               roles: ['coach'],   accent: 'brand' },
  { key: 'trainees',               path: '/trainees',                  emoji: '👥', labelKey: 'trainees',               roles: ['coach'],   accent: 'blue' },
  { key: 'templates',              path: '/templates',                 emoji: '📝', labelKey: 'templates',              roles: ['coach'] },
  { key: 'invite',                 path: '/invite',                    emoji: '➕', labelKey: 'invite',                 roles: ['coach'],   accent: 'green' },
  { key: 'my-coaches',             path: '/my-coaches',                emoji: '👨‍🏫', labelKey: 'my-coaches',             roles: ['trainee'], accent: 'blue', condition: 'hasCoaches', pinnable: false },
  { key: 'become-specialist',      path: '/become-specialist',         emoji: '🎓', labelKey: 'become-specialist',      roles: ['trainee'], accent: 'orange' },
  { key: 'my-profile',             path: '/my-profile',                emoji: '👤', labelKey: 'my-profile',             roles: ['any'] },
  { key: 'subscription',           path: '/subscription',              emoji: '💎', labelKey: 'subscription',           roles: ['any'],     accent: 'brand' },
  { key: 'support',                path: '/support',                   emoji: '🛠️', labelKey: 'support',                roles: ['any'],     accent: 'orange', pinnable: false },
  { key: 'notifications-settings', path: '/notifications-settings',    emoji: '🔔', labelKey: 'notifications-settings', roles: ['any'] },
  { key: 'quick-access',           path: '/quick-access',              emoji: '⚡', labelKey: 'quick-access',           roles: ['any'],     accent: 'brand', pinnable: false },
]

export const MORE_SECTION_CONFIGS: MoreSectionConfig[] = [
  { key: 'coachTraining',    titleKey: 'clients',          roles: ['coach'],            itemKeys: ['trainees', 'all-training-sets', 'templates', 'this-week-trainings', 'pro-view'] },
  { key: 'traineeTraining',  titleKey: 'coachTraining',    roles: ['trainee'],          itemKeys: ['my-coaches', 'active-training-set', 'this-week-trainings'], condition: 'hasCoaches' },
  { key: 'personalTraining', titleKey: 'personalTraining', roles: ['coach', 'trainee'], itemKeys: ['start-training', 'training-blocks', 'training-programs', 'program-history' /* 'training-schedule' — temporarily hidden */] },
  { key: 'account',          titleKey: 'account',          roles: ['coach', 'trainee'], itemKeys: ['my-profile', 'subscription', 'support', 'notifications-settings', 'quick-access', 'become-specialist'] },
]

const TRAINEE_WITH_ACTIVE_COACH_ITEMS = [
  'active-training-set',
  'this-week-trainings',
  'start-training',
  'program-history',
]

export function itemKeysForMoreSection(
  section: MoreSectionConfig,
  opts: { isCoach: boolean; hasActiveCoach: boolean },
): string[] {
  if (!opts.hasActiveCoach) return section.itemKeys
  if (section.key === 'traineeTraining') return TRAINEE_WITH_ACTIVE_COACH_ITEMS
  if (section.key === 'personalTraining') {
    return opts.isCoach ? ['start-training', 'program-history'] : []
  }
  return section.itemKeys
}

export const DEFAULT_SHORTCUTS: Record<'coach' | 'traineeWithCoach' | 'solo', string[]> = {
  coach:            ['this-week-trainings', 'all-training-sets', 'trainees'],
  traineeWithCoach: ['this-week-trainings', 'active-training-set', 'start-training'],
  solo:             ['start-training', 'training-blocks', 'training-programs'],
}

export function getDefaultShortcuts(hasTrainees: boolean, hasCoaches: boolean): string[] {
  if (hasTrainees) return DEFAULT_SHORTCUTS.coach
  if (hasCoaches)  return DEFAULT_SHORTCUTS.traineeWithCoach
  return DEFAULT_SHORTCUTS.solo
}

export function getItemsForRole(hasTrainees: boolean, hasCoaches = false): QuickAccessItem[] {
  return QUICK_ACCESS_CATALOG.filter(item => {
    if (item.pinnable === false) return false
    if (item.condition === 'hasCoaches' && !hasCoaches) return false
    if (item.roles.includes('any')) return true
    if (hasTrainees && item.roles.includes('coach')) return true
    if (!hasTrainees && item.roles.includes('trainee')) return true
    return false
  })
}

export function resolveShortcuts(keys: string[]): QuickAccessItem[] {
  return keys
    .map(key => QUICK_ACCESS_CATALOG.find(item => item.key === key))
    .filter((item): item is QuickAccessItem => item !== undefined && item.pinnable !== false)
}
